import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiClock, FiEye, FiEyeOff, FiMessageCircle, FiPhoneCall } from 'react-icons/fi';
import API from '../api/axios';
import './Login.css';

const formatTimer = (totalSeconds) => {
  if (totalSeconds <= 0) return '00:00';
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const phoneFromState = location.state?.phone || (() => {
    try {
      return sessionStorage.getItem('sc_reset_phone') || '';
    } catch {
      return '';
    }
  })();

  const initialCooldown = location.state?.initialCooldownSeconds || 0;

  const [phone, setPhone] = useState(phoneFromState);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(initialCooldown);
  const timerRef = useRef(null);

  // Tick down cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      timerRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [cooldownSeconds > 0]);

  // Check rate limit status on mount if not provided from state
  useEffect(() => {
    if (phone && phone.length === 10 && !initialCooldown) {
      API.get(`/auth/otp-status?phone=${phone}`)
        .then(({ data }) => {
          if (data.cooldownActive && data.remainingSeconds > 0) {
            setCooldownSeconds(data.remainingSeconds);
          }
        })
        .catch(() => {});
    }
  }, [phone]);

  const isOtpValid = otp.length >= 4 && otp.length <= 6;
  const isPhoneValid = phone.length === 10;
  const isPasswordValid = newPassword.length >= 6 && newPassword === confirmPassword;
  const canSubmit = !loading && isPhoneValid && isOtpValid && isPasswordValid;

  const handleResendOtp = async () => {
    if (!isPhoneValid || cooldownSeconds > 0 || resending) return;
    setResending(true);
    try {
      const { data } = await API.post('/auth/forgot-password', { phone });
      toast.success(data.message || 'New OTP sent!');
      if (data.devOtp) {
        toast.success(`Dev OTP: ${data.devOtp}`);
      }
      setCooldownSeconds(data.cooldownSeconds || 600);
    } catch (err) {
      const res = err.response?.data;
      if (err.response?.status === 429) {
        const secs = res?.retryAfterSeconds || (res?.retryAfterMinutes ? res.retryAfterMinutes * 60 : 600);
        setCooldownSeconds(secs);
        toast.error(res?.error || `Rate limit active. Please wait ${formatTimer(secs)}.`);
      } else {
        toast.error(res?.error || 'Failed to resend OTP');
      }
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phone || phone.length !== 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }

    if (!isOtpValid) {
      toast.error('Please enter the 4 to 6 digit OTP received');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { data } = await API.post('/auth/reset-password', {
        phone,
        otp: otp.trim(),
        newPassword
      });

      toast.success(data.message || 'Password reset successfully!');

      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-header">
        <div className="login-logo">🖨️</div>
        <h1 className="login-title">Shubham Computers</h1>
        <p className="login-tagline">Print & Stationery Mart</p>
        <p className="login-sub">Jhajjar • Delivery under 30 mins ⚡</p>
      </div>

      <div className="login-card">
        <form onSubmit={handleSubmit}>
          <h2 className="login-form-title">Reset Password</h2>
          <p className="login-otp-info">Enter the OTP sent to your phone and choose a new password.</p>

          <div className="otp-call-notice">
            <FiPhoneCall size={16} />
            <span>
              <strong>Heads up:</strong> You may receive your OTP via SMS or an <strong>automated phone call</strong>. If your phone rings, please answer to receive your code.
            </span>
          </div>

          {phoneFromState ? (
            <div className="reset-phone-badge">
              <span>Phone: <strong>+91 {phone}</strong></span>
              <button
                type="button"
                className="login-change-btn"
                onClick={() => {
                  try { sessionStorage.removeItem('sc_reset_phone'); } catch (e) {}
                  navigate('/forgot-password');
                }}
              >
                Change
              </button>
            </div>
          ) : (
            <div className="phone-input-group">
              <span className="phone-prefix">+91</span>
              <input
                type="tel"
                className="phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter phone number"
                required
                maxLength="10"
              />
            </div>
          )}

          <div style={{ marginBottom: 8 }}>
            <input
              type="text"
              className="otp-input-field"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 4-digit OTP"
              required
              maxLength="6"
              autoFocus
            />
          </div>

          <div className="resend-row">
            <span>Didn't receive the OTP?</span>
            {cooldownSeconds > 0 ? (
              <span style={{ color: '#ffab00', fontWeight: '600', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiClock size={13} />
                Resend in {formatTimer(cooldownSeconds)}
              </span>
            ) : (
              <button
                type="button"
                className="resend-btn"
                onClick={handleResendOtp}
                disabled={resending}
              >
                {resending ? 'Sending...' : 'Resend OTP'}
              </button>
            )}
          </div>

          <div className="password-input-container" style={{ marginTop: 12 }}>
            <input
              type={showPassword ? 'text' : 'password'}
              className="input-field password-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 6 chars)"
              required
              minLength="6"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>

          <div className="password-input-container">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              className="input-field password-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength="6"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full login-submit-btn"
            disabled={!canSubmit}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>

          <div>
            <Link to="/login" className="back-to-login">
              <FiArrowLeft size={16} />
              <span>Back to Login</span>
            </Link>
          </div>

          <div className="whatsapp-help-box">
            <p className="whatsapp-help-text">Did not receive SMS OTP? Contact Shubham Cyber Cafe directly:</p>
            <a
              href={`https://wa.me/919350336367?text=${encodeURIComponent(`Hi Shubham, I need help resetting my password on ShubInk for phone: ${phone || ''}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-help-btn"
            >
              <FiMessageCircle size={16} />
              <span>Get Help on WhatsApp</span>
            </a>
          </div>
        </form>
      </div>

      <p className="login-footer">
        By continuing, you agree to our <Link to="/terms" className="privacy-link">Terms</Link> & <Link to="/privacy-policy" className="privacy-link">Privacy Policy</Link>
      </p>
    </div>
  );
};

export default ResetPassword;
